import { reportWorkflow, newAttemptId } from "../observability/telemetry.js";
import { isExpectedValidationResponse } from "../observability/apiRequestContext.js";
import { useEffect, useMemo, useRef, useState } from "react";
import * as CheckCircleIconModule from "@mui/icons-material/CheckCircle.js";
import * as CloudDownloadIconModule from "@mui/icons-material/CloudDownload.js";
import * as ErrorOutlineIconModule from "@mui/icons-material/ErrorOutline.js";
import * as FileUploadIconModule from "@mui/icons-material/FileUpload.js";
import * as RuleIconModule from "@mui/icons-material/Rule.js";
import * as TableViewIconModule from "@mui/icons-material/TableView.js";
import Page from "../Components/Page";
import AggregatorSelectedGrowerPanel, {
  AggregatorScopeWarning,
  useSelectedAggregatorGrower,
} from "../Components/AggregatorSelectedGrowerPanel.jsx";
import {
  GrowerEmptyState,
  GrowerSectionHeading,
  GrowerSurface,
} from "../Components/GrowerLayout.jsx";
import { axiosInstance } from "../Components/axiosInstance.js";
import {
  APPLY_OUTCOME_LABELS,
  EXCEL_ENTITY_LABELS,
  PREVIEW_STATUS_LABELS,
  RECONCILIATION_ACTIONS,
  flattenDiffs,
  formatExcelFieldValue,
  groupExcelRowsBySheet,
  makeExcelRowKey,
  pairApplyResultsWithPreview,
  summarizeApplyResults,
  summarizePreviewRows,
} from "../utils/excelExchange.js";
import { trackEvent } from "../analytics/ga4.js";
import {
  DATA_READ_SCOPE,
  IMPORT_WRITE_SCOPE,
  RECONCILIATION_WRITE_SCOPE,
  selectedGrowerHasAnyScope,
  selectedGrowerHasScope,
} from "../portal/aggregatorGrowerContext.js";

const STATUS_TONES = {
  matched: "border-[#d8a31a] bg-[#fff8e5] text-[#7a5200]",
  new: "border-[#34a853] bg-[#eef8f1] text-[#1d6f38]",
  ambiguous: "border-[#f15d22] bg-[#fff3ed] text-[#9a3514]",
  missing_dependency: "border-[#f15d22] bg-[#fff3ed] text-[#9a3514]",
  invalid: "border-[#c0262d] bg-[#fff1f2] text-[#9f1239]",
};

const OUTCOME_TONES = {
  created: "border-[#34a853] bg-[#eef8f1] text-[#1d6f38]",
  updated: "border-[#34a853] bg-[#eef8f1] text-[#1d6f38]",
  case_opened: "border-[#d8a31a] bg-[#fff8e5] text-[#7a5200]",
  unchanged: "border-slate-300 bg-slate-50 text-slate-700",
  skipped_error: "border-[#c0262d] bg-[#fff1f2] text-[#9f1239]",
};

const EXCEL_SCOPE_OPTIONS = [
  { value: "farms", label: "Farms" },
  { value: "fields", label: "Fields" },
  { value: "equipment", label: "Equipment" },
  { value: "fertilizers", label: "Fertilizers" },
  { value: "crop_years", label: "Crop years" },
];

const APPLY_STRATEGY_OPTIONS = [
  {
    value: "update",
    label: "Update existing records",
    description: "Apply incoming values and preserve existing values where workbook cells are blank.",
  },
  {
    value: "override",
    label: "Override existing records",
    description: "Apply incoming values and clear eligible existing fields when workbook cells are blank.",
  },
];

function resolveMuiIcon(iconModule) {
  let candidate = iconModule;

  for (let index = 0; index < 4; index += 1) {
    if (typeof candidate === "function" || candidate?.$$typeof) {
      return candidate;
    }
    candidate = candidate?.default;
  }

  return iconModule;
}

const CheckCircleIcon = resolveMuiIcon(CheckCircleIconModule);
const CloudDownloadIcon = resolveMuiIcon(CloudDownloadIconModule);
const ErrorOutlineIcon = resolveMuiIcon(ErrorOutlineIconModule);
const FileUploadIcon = resolveMuiIcon(FileUploadIconModule);
const RuleIcon = resolveMuiIcon(RuleIconModule);
const TableViewIcon = resolveMuiIcon(TableViewIconModule);

function ExcelButton({
  children,
  disabled = false,
  icon = null,
  onClick,
  tone = "primary",
  type = "button",
}) {
  const toneClass =
    tone === "secondary"
      ? "border-slate-300 bg-white text-slate-700 hover:bg-slate-50"
      : tone === "danger"
        ? "border-[#c0262d] bg-white text-[#9f1239] hover:bg-[#fff1f2]"
        : "border-[#f15d22] bg-[#f15d22] text-white hover:bg-[#d94818]";

  return (
    <button
      className={`inline-flex min-h-[42px] items-center justify-center gap-2 rounded-[4px] border px-4 py-2 text-sm font-bold transition disabled:cursor-not-allowed disabled:border-slate-200 disabled:bg-slate-100 disabled:text-slate-400 ${toneClass}`}
      disabled={disabled}
      onClick={onClick}
      type={type}
    >
      {icon}
      <span>{children}</span>
    </button>
  );
}

function StatusBadge({ children, toneClass }) {
  return (
    <span
      className={`inline-flex items-center rounded-full border px-2.5 py-1 text-[12px] font-bold uppercase tracking-[0.08em] ${toneClass}`}
    >
      {children}
    </span>
  );
}

function MetricCard({ label, value, tone = "neutral" }) {
  const toneClass =
    tone === "green"
      ? "border-[#34a853]/40 bg-[#eef8f1]"
      : tone === "orange"
        ? "border-[#f15d22]/35 bg-[#fff3ed]"
        : tone === "gold"
          ? "border-[#d8a31a]/40 bg-[#fff8e5]"
          : "border-slate-200 bg-white";

  return (
    <div className={`rounded-[8px] border px-4 py-3 ${toneClass}`}>
      <div className="text-[12px] font-bold uppercase tracking-[0.14em] text-slate-500">
        {label}
      </div>
      <div className="mt-1 text-2xl font-black text-slate-900">{value}</div>
    </div>
  );
}

function getDownloadFilename(response, fallback) {
  const disposition = response?.headers?.["content-disposition"] || "";
  const utf8Match = disposition.match(/filename\*=UTF-8''([^;]+)/i);
  const plainMatch = disposition.match(/filename="?([^";]+)"?/i);
  const rawFilename = utf8Match?.[1] || plainMatch?.[1];

  if (!rawFilename) {
    return fallback;
  }

  try {
    return decodeURIComponent(rawFilename);
  } catch (error) {
    return rawFilename;
  }
}

function getApiErrorMessage(error, fallback) {
  const detail = error?.response?.data?.detail;
  if (typeof detail === "string" && detail.trim()) {
    return detail;
  }
  if (Array.isArray(detail)) {
    const messages = detail
      .map((item) => item?.msg || item?.message)
      .filter(Boolean);
    if (messages.length) {
      return messages.join(" ");
    }
  }
  if (detail && typeof detail === "object") {
    return detail.message || detail.error || fallback;
  }
  return fallback;
}

function downloadBlob(blob, filename) {
  const url = window.URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  window.URL.revokeObjectURL(url);
}

function NaturalKey({ value }) {
  const entries = Object.entries(value || {});

  if (!entries.length) {
    return <span className="text-slate-400">Not available</span>;
  }

  return (
    <div className="flex flex-wrap gap-1.5">
      {entries.map(([key, entryValue]) => (
        <span
          className="rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-[12px] font-semibold text-slate-700"
          key={key}
        >
          {key}: {formatExcelFieldValue(key, entryValue)}
        </span>
      ))}
    </div>
  );
}

function DiffList({ diffs, compact = false }) {
  const rows = flattenDiffs(diffs);

  if (!rows.length) {
    return <span className="text-slate-400">No field changes</span>;
  }

  return (
    <div className={compact ? "space-y-2" : "space-y-3"}>
      {rows.map((diff) => (
        <div
          className="min-w-0 rounded-[8px] border border-slate-200 bg-white p-3"
          key={diff.fieldName}
        >
          <div className="text-[12px] font-bold uppercase tracking-[0.12em] text-slate-500">
            {diff.fieldName}
          </div>
          <div className="mt-2 grid min-w-0 gap-3 text-sm md:grid-cols-2">
            <div className="min-w-0">
              <div className="font-bold text-slate-500">Current:</div>
              <span className="mt-1 block max-h-32 min-w-0 overflow-auto whitespace-pre-wrap break-words rounded bg-slate-50 p-2 font-mono text-xs leading-5 text-slate-800">
                {formatExcelFieldValue(diff.fieldName, diff.current)}
              </span>
            </div>
            <div className="min-w-0">
              <div className="font-bold text-slate-500">Incoming:</div>
              <span className="mt-1 block max-h-32 min-w-0 overflow-auto whitespace-pre-wrap break-words rounded bg-slate-50 p-2 font-mono text-xs leading-5 text-slate-900">
                {formatExcelFieldValue(diff.fieldName, diff.incoming)}
              </span>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

function Messages({ errors = [], warnings = [] }) {
  if (!errors.length && !warnings.length) {
    return null;
  }

  return (
    <div className="space-y-1 text-sm">
      {errors.map((error, index) => (
        <div className="font-semibold text-[#9f1239]" key={`error-${index}`}>
          {error}
        </div>
      ))}
      {warnings.map((warning, index) => (
        <div className="font-semibold text-[#7a5200]" key={`warning-${index}`}>
          {warning}
        </div>
      ))}
    </div>
  );
}

function PreviewRows({ rows }) {
  const groupedRows = groupExcelRowsBySheet(rows);

  return (
    <div className="space-y-4">
      {Object.entries(groupedRows).map(([sheet, sheetRows]) => (
        <div
          className="overflow-hidden rounded-[8px] border border-slate-200 bg-white"
          key={sheet}
        >
          <div className="flex items-center justify-between gap-3 border-b border-slate-200 bg-[#fcfcfa] px-4 py-3">
            <h3 className="text-lg font-black text-slate-900">{sheet}</h3>
            <span className="text-sm font-semibold text-slate-500">
              {sheetRows.length} rows
            </span>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-[980px] w-full table-fixed text-left text-sm">
              <thead className="bg-slate-50 text-slate-500">
                <tr>
                  <th className="w-[82px] px-3 py-3 font-bold">Row</th>
                  <th className="w-[160px] px-3 py-3 font-bold">Entity</th>
                  <th className="w-[170px] px-3 py-3 font-bold">Status</th>
                  <th className="w-[260px] px-3 py-3 font-bold">Match Key</th>
                  <th className="px-3 py-3 font-bold">Changes</th>
                </tr>
              </thead>
              <tbody>
                {sheetRows.map((row) => (
                  <tr
                    className="border-t border-slate-200 align-top"
                    key={makeExcelRowKey(row)}
                  >
                    <td className="px-3 py-3 font-bold text-slate-700">
                      {row.row_index}
                    </td>
                    <td className="px-3 py-3 text-slate-700">
                      {EXCEL_ENTITY_LABELS[row.entity_type] || row.entity_type}
                    </td>
                    <td className="px-3 py-3">
                      <StatusBadge
                        toneClass={
                          STATUS_TONES[row.match_status] || STATUS_TONES.invalid
                        }
                      >
                        {PREVIEW_STATUS_LABELS[row.match_status] ||
                          row.match_status}
                      </StatusBadge>
                    </td>
                    <td className="px-3 py-3">
                      <NaturalKey value={row.natural_key} />
                    </td>
                    <td className="space-y-3 px-3 py-3">
                      <DiffList compact diffs={row.diffs} />
                      {row.blank_override_fields?.length ? (
                        <div className="rounded-[8px] border border-[#d8a31a]/40 bg-[#fff8e5] p-3 text-sm text-[#7a5200]">
                          Override would clear:{" "}
                          {row.blank_override_fields.join(", ")}
                        </div>
                      ) : null}
                      <Messages errors={row.errors} warnings={row.warnings} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ))}
    </div>
  );
}

function ApplyResults({
  canResolve = true,
  results,
  previewRows,
  strategy,
  resolvedCases,
  resolvingCase,
  onResolveCase,
}) {
  const pairedRows = pairApplyResultsWithPreview(results, previewRows);
  const conflicts = pairedRows.filter(
    ({ result }) => result.outcome === "case_opened"
  );

  return (
    <div className="space-y-5">
      <div className="overflow-hidden rounded-[8px] border border-slate-200 bg-white">
        <div className="border-b border-slate-200 bg-[#fcfcfa] px-4 py-3">
          <h3 className="text-lg font-black text-slate-900">Apply Results</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-[900px] w-full table-fixed text-left text-sm">
            <thead className="bg-slate-50 text-slate-500">
              <tr>
                <th className="w-[120px] px-3 py-3 font-bold">Sheet</th>
                <th className="w-[82px] px-3 py-3 font-bold">Row</th>
                <th className="w-[160px] px-3 py-3 font-bold">Entity</th>
                <th className="w-[170px] px-3 py-3 font-bold">Outcome</th>
                <th className="px-3 py-3 font-bold">Details</th>
              </tr>
            </thead>
            <tbody>
              {pairedRows.map(({ result, preview }) => (
                <tr
                  className="border-t border-slate-200 align-top"
                  key={makeExcelRowKey(result)}
                >
                  <td className="px-3 py-3 text-slate-700">{result.sheet}</td>
                  <td className="px-3 py-3 font-bold text-slate-700">
                    {result.row_index}
                  </td>
                  <td className="px-3 py-3 text-slate-700">
                    {EXCEL_ENTITY_LABELS[result.entity_type] ||
                      result.entity_type}
                  </td>
                  <td className="px-3 py-3">
                    <StatusBadge
                      toneClass={
                        OUTCOME_TONES[result.outcome] ||
                        OUTCOME_TONES.skipped_error
                      }
                    >
                      {APPLY_OUTCOME_LABELS[result.outcome] || result.outcome}
                    </StatusBadge>
                  </td>
                  <td className="space-y-2 px-3 py-3 text-slate-700">
                    {result.reconciliation_case_public_id ? (
                      <div>
                        Case:{" "}
                        <span className="font-mono text-xs">
                          {result.reconciliation_case_public_id}
                        </span>
                      </div>
                    ) : null}
                    {result.public_id ? (
                      <div>
                        Record:{" "}
                        <span className="font-mono text-xs">
                          {result.public_id}
                        </span>
                      </div>
                    ) : null}
                    <Messages errors={result.errors} />
                    {preview?.blank_override_fields?.length &&
                    result.outcome !== "skipped_error" ? (
                      <div className="rounded-[8px] border border-[#d8a31a]/40 bg-[#fff8e5] p-3 text-sm text-[#7a5200]">
                        {result.outcome === "updated" && strategy === "override"
                          ? "Cleared from blank workbook fields: "
                          : result.outcome === "updated"
                            ? "Preserved because workbook fields were blank: "
                            : "Override would clear: "}
                        {preview.blank_override_fields.join(", ")}
                      </div>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {conflicts.length ? (
        <GrowerSurface padding="p-5" tone="subtle">
          <div className="flex flex-col gap-2 md:flex-row md:items-start md:justify-between">
            <div>
              <h3 className="text-xl font-black text-slate-900">
                Conflicts Need A Decision
              </h3>
              <p className="mt-1 text-sm leading-6 text-slate-600">
                These rows matched existing records and opened reconciliation
                cases. No existing record changed until a choice is saved.
              </p>
            </div>
            <StatusBadge toneClass="border-[#d8a31a] bg-[#fff8e5] text-[#7a5200]">
              {conflicts.length} open
            </StatusBadge>
          </div>

          <div className="mt-5 grid gap-4">
            {conflicts.map(({ result, preview }) => {
              const caseId = result.reconciliation_case_public_id;
              const resolved = resolvedCases[caseId];
              const resolving = resolvingCase === caseId;

              return (
                <div
                  className="rounded-[8px] border border-slate-200 bg-white p-4"
                  key={caseId}
                >
                  <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
                    <div>
                      <div className="text-[12px] font-bold uppercase tracking-[0.12em] text-slate-500">
                        {result.sheet} row {result.row_index}
                      </div>
                      <h4 className="mt-1 text-lg font-black text-slate-900">
                        {EXCEL_ENTITY_LABELS[result.entity_type] ||
                          result.entity_type}
                      </h4>
                    </div>
                    {resolved ? (
                      <StatusBadge toneClass="border-[#34a853] bg-[#eef8f1] text-[#1d6f38]">
                        {resolved}
                      </StatusBadge>
                    ) : null}
                  </div>

                  <div className="mt-4">
                    <DiffList diffs={preview?.diffs} />
                  </div>

                  {preview?.blank_override_fields?.length ? (
                    <div className="mt-4 rounded-[8px] border border-[#d8a31a]/40 bg-[#fff8e5] p-3 text-sm text-[#7a5200]">
                      Override also clears blank workbook fields:{" "}
                      {preview.blank_override_fields.join(", ")}
                    </div>
                  ) : null}

                  {!canResolve ? (
                    <div className="mt-4">
                      <AggregatorScopeWarning
                        message="This selected grower link cannot resolve import conflicts."
                        requiredScope={RECONCILIATION_WRITE_SCOPE}
                      />
                    </div>
                  ) : null}

                  <div className="mt-4 flex flex-wrap gap-2">
                    <ExcelButton
                      disabled={Boolean(resolved) || resolving || !canResolve}
                      icon={<CheckCircleIcon fontSize="small" />}
                      onClick={() =>
                        onResolveCase(caseId, RECONCILIATION_ACTIONS.update)
                      }
                    >
                      Update
                    </ExcelButton>
                    <ExcelButton
                      disabled={Boolean(resolved) || resolving || !canResolve}
                      icon={<RuleIcon fontSize="small" />}
                      onClick={() =>
                        onResolveCase(caseId, RECONCILIATION_ACTIONS.override)
                      }
                      tone="secondary"
                    >
                      Override
                    </ExcelButton>
                    <ExcelButton
                      disabled={Boolean(resolved) || resolving || !canResolve}
                      icon={<ErrorOutlineIcon fontSize="small" />}
                      onClick={() =>
                        onResolveCase(caseId, RECONCILIATION_ACTIONS.discard)
                      }
                      tone="danger"
                    >
                      Discard
                    </ExcelButton>
                  </div>
                </div>
              );
            })}
          </div>
        </GrowerSurface>
      ) : null}
    </div>
  );
}

export default function ExcelExchangePage({ requireSelectedGrower = false }) {
  const fileInputRef = useRef(null);
  const applyResultsRef = useRef(null);
  const previewRequestRef = useRef(0);
  const dragDepthRef = useRef(0);
  const selectedAggregatorGrower = useSelectedAggregatorGrower();
  const [activeTab, setActiveTab] = useState("import");
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewRows, setPreviewRows] = useState([]);
  const [diagnostics, setDiagnostics] = useState([]);
  const [attention, setAttention] = useState([]);
  const [previewToken, setPreviewToken] = useState("");
  const [applyResults, setApplyResults] = useState([]);
  const [statusMessage, setStatusMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [downloadState, setDownloadState] = useState("");
  const [previewState, setPreviewState] = useState("idle");
  const [applyState, setApplyState] = useState("idle");
  const [applyStrategy, setApplyStrategy] = useState("update");
  const [hasPreviewed, setHasPreviewed] = useState(false);
  const [overrideConfirmed, setOverrideConfirmed] = useState(false);
  const [selectedScopes, setSelectedScopes] = useState(
    EXCEL_SCOPE_OPTIONS.map((option) => option.value)
  );
  const [errorsAcknowledged, setErrorsAcknowledged] = useState(false);
  const [resolvedCases, setResolvedCases] = useState({});
  const [resolvingCase, setResolvingCase] = useState("");
  const [isDraggingWorkbook, setIsDraggingWorkbook] = useState(false);
  const canReadSelectedGrower =
    !requireSelectedGrower ||
    selectedGrowerHasScope(selectedAggregatorGrower, DATA_READ_SCOPE);
  const canImportForSelectedGrower =
    !requireSelectedGrower ||
    selectedGrowerHasScope(selectedAggregatorGrower, IMPORT_WRITE_SCOPE);
  const canResolveForSelectedGrower =
    !requireSelectedGrower ||
    selectedGrowerHasScope(selectedAggregatorGrower, RECONCILIATION_WRITE_SCOPE);
  const canUseExcelForSelectedGrower =
    !requireSelectedGrower ||
    selectedGrowerHasAnyScope(selectedAggregatorGrower, [
      DATA_READ_SCOPE,
      IMPORT_WRITE_SCOPE,
    ]);
  const canSelectWorkbook =
    (!requireSelectedGrower || Boolean(selectedAggregatorGrower)) &&
    canImportForSelectedGrower && selectedScopes.length > 0 && applyState !== "applying";

  const previewSummary = useMemo(
    () => summarizePreviewRows(previewRows),
    [previewRows]
  );
  const applySummary = useMemo(
    () => summarizeApplyResults(applyResults),
    [applyResults]
  );
  const hasPreviewErrors = previewSummary.errors > 0;
  const blankOverrideCount = useMemo(
    () =>
      previewRows.reduce(
        (total, row) => total + (row.blank_override_fields?.length || 0),
        0
      ),
    [previewRows]
  );
  const actionablePreviewCount = useMemo(
    () =>
      previewRows.filter((row) => {
        if (row.match_status === "new") return true;
        if (row.match_status !== "matched") return false;
        if (Object.keys(row.diffs || {}).length > 0) return true;
        return (
          applyStrategy === "override" &&
          (row.blank_override_fields?.length || 0) > 0
        );
      }).length,
    [applyStrategy, previewRows]
  );
  const canApply =
    (!requireSelectedGrower || Boolean(selectedAggregatorGrower)) &&
    canImportForSelectedGrower &&
    selectedFile &&
    selectedScopes.length > 0 &&
    hasPreviewed &&
    actionablePreviewCount > 0 &&
    applyState !== "applying" &&
    previewState !== "loading" &&
    (!(hasPreviewErrors || diagnostics.length) || errorsAcknowledged) &&
    (applyStrategy !== "override" ||
      blankOverrideCount === 0 ||
      overrideConfirmed);

  const resetImportState = (file) => {
    dragDepthRef.current = 0;
    setIsDraggingWorkbook(false);
    previewRequestRef.current += 1;
    setPreviewState("idle");
    setSelectedFile(file);
    setPreviewRows([]);
    setDiagnostics([]);
    setAttention([]);
    setPreviewToken("");
    setHasPreviewed(false);
    setApplyResults([]);
    setResolvedCases({});
    setErrorsAcknowledged(false);
    setOverrideConfirmed(false);
    setStatusMessage("");
    setErrorMessage("");
  };

  useEffect(() => {
    resetImportState(null);
    return () => { previewRequestRef.current += 1; };
    // A selected-grower change invalidates both the file and in-flight preview.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedAggregatorGrower?.grower_profile_public_id, canImportForSelectedGrower]);

  const buildFormData = ({ includeStrategy = false, file = selectedFile, scopes = selectedScopes } = {}) => {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("include", scopes.join(","));
    if (includeStrategy) {
      formData.append("strategy", applyStrategy);
      if (previewToken) formData.append("preview_token", previewToken);
      formData.append("acknowledge_diagnostics", String(errorsAcknowledged));
    }
    return formData;
  };

  const toggleScope = (scope) => {
    const scopes = selectedScopes.includes(scope)
      ? selectedScopes.filter((item) => item !== scope)
      : [...selectedScopes, scope];
    setSelectedScopes(scopes);
    resetImportState(selectedFile);
    if (selectedFile && scopes.length) handlePreview(selectedFile, scopes);
  };

  const chooseWorkbook = (files) => {
    if (!canSelectWorkbook) return;
    const incoming = Array.from(files || []);
    const file = incoming[0];
    let error = "";
    if (incoming.length !== 1) error = "Choose or drop one Excel workbook at a time.";
    else if (!/\.xlsx$/i.test(file.name)) error = "Choose an .xlsx Excel workbook.";
    else if (file.size === 0) error = "This workbook is empty. Choose a saved .xlsx workbook.";
    else if (file.size > 20 * 1024 * 1024) error = "This workbook exceeds the 20 MB limit.";
    resetImportState(error ? null : file);
    if (error) setErrorMessage(error);
    else handlePreview(file);
  };

  const openWorkbookPicker = () => {
    if (canSelectWorkbook && fileInputRef.current) {
      fileInputRef.current.value = "";
      fileInputRef.current.click();
    }
  };

  const resetWorkbookDrag = () => {
    dragDepthRef.current = 0;
    setIsDraggingWorkbook(false);
  };

  const handleDownload = async (kind) => {
    if (requireSelectedGrower && !selectedAggregatorGrower) {
      setErrorMessage("Select a grower before downloading a workbook.");
      return;
    }
    const canDownload =
      kind === "template"
        ? canImportForSelectedGrower
        : canReadSelectedGrower;
    if (!canDownload) {
      setErrorMessage(
        kind === "template"
          ? "This grower link cannot download import templates."
          : "This grower link cannot export grower data."
      );
      return;
    }
    if (selectedScopes.length === 0) {
      setErrorMessage("Select at least one data scope before downloading.");
      return;
    }

    const endpoint = kind === "template" ? "template" : "export";
    const fallback =
      kind === "template"
        ? "fieldprint-import-template.xlsx"
        : "fieldprint-export.xlsx";

    setDownloadState(kind);
    setErrorMessage("");

    try {
      const response = await axiosInstance.get(
        `${process.env.REACT_APP_API_URL}/excel/${endpoint}`,
        {
          params: { include: selectedScopes.join(",") },
          responseType: "blob",
        }
      );
      downloadBlob(response.data, getDownloadFilename(response, fallback));
      setStatusMessage(
        kind === "template"
          ? "Excel import template downloaded."
          : "Current grower data exported to Excel."
      );
      trackEvent("excel_exchange_downloaded", {
        workflow_area: "excel_exchange",
        download_type: kind,
      });
    } catch (error) {
      console.error("Failed to download Excel workbook", error);
      setErrorMessage(
        getApiErrorMessage(
          error,
          "The Excel workbook could not be downloaded."
        )
      );
    } finally {
      setDownloadState("");
    }
  };

  const handlePreview = async (file = selectedFile, scopes = selectedScopes, { notice = "" } = {}) => {
    if (requireSelectedGrower && !selectedAggregatorGrower) {
      setErrorMessage("Select a grower before previewing a workbook.");
      return;
    }
    if (!canImportForSelectedGrower) {
      setErrorMessage("This grower link cannot run import previews.");
      return;
    }

    if (!file) {
      setErrorMessage("Choose an .xlsx file before previewing.");
      return;
    }
    if (scopes.length === 0) {
      setErrorMessage("Select at least one data scope before previewing.");
      return;
    }

    const attemptId = newAttemptId();
    const request = ++previewRequestRef.current;
    const started = Date.now();
    setPreviewState("loading");
    setHasPreviewed(false);
    setPreviewRows([]);
    setOverrideConfirmed(false);
    setDiagnostics([]);
    setAttention([]);
    setPreviewToken("");
    setApplyResults([]);
    setResolvedCases({});
    setErrorsAcknowledged(false);
    setErrorMessage(notice);
    setStatusMessage("");

    try {
      let response;
      try {
        response = await axiosInstance.post(`${process.env.REACT_APP_API_URL}/excel/import/preview-v2`, buildFormData({ file, scopes }));
      } catch (error) {
        if (error?.response?.status !== 404) throw error;
        if (request !== previewRequestRef.current) return;
        response = await axiosInstance.post(`${process.env.REACT_APP_API_URL}/excel/import/preview`, buildFormData({ file, scopes }));
      }
      if (request !== previewRequestRef.current) return;
      const detailed = !Array.isArray(response.data) && Array.isArray(response.data?.rows);
      const rows = detailed ? response.data.rows : (Array.isArray(response.data) ? response.data : []);
      setDiagnostics(detailed ? response.data.diagnostics || [] : []);
      setPreviewToken(detailed ? response.data.preview_token || "" : "");
      setAttention([]);
      reportWorkflow({ workflow: "excel_preview", outcome: rows.some(row => row.errors?.length) || response.data?.diagnostics?.length ? "blocked" : "succeeded", reason: rows.some(row => row.errors?.length) || response.data?.diagnostics?.length ? "import_partial_result" : "completed", attemptId, requestId: response.headers?.["x-request-id"], durationMs: Date.now() - started });
      setPreviewRows(rows);
      setHasPreviewed(true);
      setStatusMessage(
        rows.length
          ? `Preview complete. ${rows.length} workbook rows checked.`
          : "Preview complete, but no data rows were found in the selected scopes."
      );
      trackEvent("excel_exchange_previewed", {
        workflow_area: "excel_exchange",
        row_count: rows.length,
      });
    } catch (error) {
      if (request !== previewRequestRef.current) return;
      reportWorkflow({workflow:"excel_preview", outcome:[400,401,403,409,413].includes(error.response?.status) || isExpectedValidationResponse(error.response) ? "blocked" : "failed", reason:"request_failed", attemptId, requestId:error.response?.headers?.["x-request-id"]});
      console.error("Failed to preview Excel import", error);
      setPreviewRows([]);
      setHasPreviewed(false);
      setErrorMessage(
        getApiErrorMessage(
          error,
          "The selected workbook could not be previewed. Drop or choose it again to retry."
        )
      );
    } finally {
      if (request === previewRequestRef.current) setPreviewState("idle");
    }
  };

  const handleApply = async () => {
    if (!canApply) {
      return;
    }

    const attemptId = newAttemptId();
    const previewVersion = previewRequestRef.current;
    const started = Date.now();
    setApplyState("applying");
    setApplyResults([]);
    setResolvedCases({});
    setErrorMessage("");
    setStatusMessage("");

    try {
      const response = await axiosInstance.post(
        `${process.env.REACT_APP_API_URL}/excel/import/${previewToken ? "apply-v2" : "apply"}`,
        buildFormData({ includeStrategy: true })
      );
      if (previewVersion !== previewRequestRef.current) return;
      const detailed = !Array.isArray(response.data) && Array.isArray(response.data?.rows);
      const rows = detailed ? response.data.rows : (Array.isArray(response.data) ? response.data : []);
      setAttention(detailed ? response.data.attention || [] : []);
      if (detailed) setPreviewToken(response.data.preview_token || "");
      const summary = summarizeApplyResults(rows);
      const needsAttention = Boolean(summary.outcomes.skipped_error || summary.outcomes.case_opened || response.data?.attention?.length);
      reportWorkflow({workflow:"excel_apply", outcome:needsAttention ? "blocked" : "succeeded", reason:needsAttention ? "import_partial_result" : "completed", attemptId, requestId:response.headers?.["x-request-id"], durationMs:Date.now()-started, counts:{...summary.outcomes, attention:response.data?.attention?.length || 0}});
      setApplyResults(rows);
      setStatusMessage(
        `Import applied. ${summary.outcomes.created || 0} created, ${summary.outcomes.updated || 0} updated, ${summary.outcomes.unchanged || 0} unchanged, ${summary.outcomes.case_opened || 0} need review, and ${summary.outcomes.skipped_error || 0} skipped.`
      );
      trackEvent("excel_exchange_applied", {
        workflow_area: "excel_exchange",
        row_count: rows.length,
        apply_strategy: applyStrategy,
      });
      window.setTimeout(() => {
        applyResultsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 0);
    } catch (error) {
      if (previewVersion !== previewRequestRef.current) return;
      reportWorkflow({workflow:"excel_apply", outcome:!error.response ? "unknown" : [400,401,403,409,413].includes(error.response.status) || isExpectedValidationResponse(error.response) ? "blocked" : "failed", reason:!error.response ? "persistence_unknown" : "request_failed", attemptId, requestId:error.response?.headers?.["x-request-id"]});
      console.error("Failed to apply Excel import", error);
      const needsFreshPreview = !error.response || error?.response?.status >= 500 || error?.response?.status === 409;
      if (needsFreshPreview) {
        setHasPreviewed(false);
        setPreviewToken("");
      }
      const notice = !error.response || error.response.status >= 500
        ? "The import result could not be confirmed. Review the refreshed preview before applying again."
        : error.response.status === 409
          ? "Saved records changed after the last preview. Review the refreshed changes before applying again."
          : getApiErrorMessage(error, "The selected workbook could not be applied.");
      setErrorMessage(notice);
      if (needsFreshPreview && previewVersion === previewRequestRef.current) {
        await handlePreview(selectedFile, selectedScopes, { notice });
      }
    } finally {
      setApplyState("idle");
    }
  };

  const handleResolveCase = async (caseId, action) => {
    if (requireSelectedGrower && !selectedAggregatorGrower) {
      setErrorMessage("Select a grower before resolving a conflict.");
      return;
    }
    if (!canResolveForSelectedGrower) {
      setErrorMessage("This grower link cannot resolve reconciliation cases.");
      return;
    }

    if (!caseId || !action) {
      return;
    }

    setResolvingCase(caseId);
    setErrorMessage("");

    try {
      await axiosInstance.post(
        `${process.env.REACT_APP_API_URL}/reconciliation/cases/${caseId}/resolve`,
        { action }
      );
      const label =
        action === RECONCILIATION_ACTIONS.discard
          ? "Discarded"
          : action === RECONCILIATION_ACTIONS.override
            ? "Overridden"
            : "Updated";
      setResolvedCases((current) => ({ ...current, [caseId]: label }));
      setStatusMessage(`Conflict ${caseId} resolved.`);
      trackEvent("excel_exchange_conflict_resolved", {
        workflow_area: "excel_exchange",
        resolution_action: action,
      });
    } catch (error) {
      console.error("Failed to resolve reconciliation case", error);
      setErrorMessage(
        getApiErrorMessage(
          error,
          "The reconciliation case could not be resolved."
        )
      );
    } finally {
      setResolvingCase("");
    }
  };

  return (
    <Page
      title="Excel Exchange"
      headerVariant="hybrid"
      headerBorderColor="border-[#34a853]"
      contentWidth="wide"
      padding="px-4 py-5 md:px-8 md:py-7"
      introContent={
        <span>
          Export and import grower records with row-level validation and
          controlled updates.
        </span>
      }
    >
      <div className="w-full space-y-5">
        {requireSelectedGrower ? (
          <AggregatorSelectedGrowerPanel
            selectedGrower={selectedAggregatorGrower}
            compact
          />
        ) : null}

        {requireSelectedGrower &&
        selectedAggregatorGrower &&
        !canUseExcelForSelectedGrower ? (
          <AggregatorScopeWarning
            message="This selected grower link needs View records or Run imports to use Excel exchange workflows."
          />
        ) : null}

        {requireSelectedGrower &&
        (!selectedAggregatorGrower || !canUseExcelForSelectedGrower) ? null : (
          <>
        <GrowerSurface padding="p-5">
          <GrowerSectionHeading
            eyebrow="Data Exchange"
            title="Excel Workbook Tools"
            description="Use the import template for new data entry, or export current records before editing and re-importing. Preview is read-only; Apply saves matched rows using your selected Update or Override strategy."
            actions={
              <div className="flex flex-wrap gap-2">
                <ExcelButton
                  disabled={
                    downloadState === "template" ||
                    !canImportForSelectedGrower ||
                    selectedScopes.length === 0
                  }
                  icon={<CloudDownloadIcon fontSize="small" />}
                  onClick={() => handleDownload("template")}
                  tone="secondary"
                >
                  {downloadState === "template"
                    ? "Downloading..."
                    : "Import Template"}
                </ExcelButton>
                <ExcelButton
                  disabled={
                    downloadState === "export" ||
                    !canReadSelectedGrower ||
                    selectedScopes.length === 0
                  }
                  icon={<TableViewIcon fontSize="small" />}
                  onClick={() => handleDownload("export")}
                >
                  {downloadState === "export" ? "Exporting..." : "Export Data"}
                </ExcelButton>
              </div>
            }
          />

          <div className="mt-4 grid gap-3 border-y border-slate-200 py-3 text-sm text-slate-600 md:grid-cols-3">
            <p>
              <span className="font-bold text-slate-900">Supported:</span>{" "}
              records, boundaries, operations, soil tests, fertilizer rates,
              and 4R assessments. Model inputs, daily climate and additional
              combines use ordinary spreadsheet rows.
            </p>
            <p>
              <span className="font-bold text-slate-900">Preview:</span>{" "}
              validation never saves data.
            </p>
            <p>
              <span className="font-bold text-slate-900">Matched rows:</span>{" "}
              choose Update or Override before applying.
            </p>
          </div>

          <fieldset className="mt-4">
            <legend className="text-sm font-bold text-slate-900">
              Data scopes
            </legend>
            <p className="mt-1 text-sm text-slate-600">
              These scopes control the sheets included in downloads and the
              sheets processed during preview and apply.
            </p>
            <div className="mt-3 grid gap-2 sm:grid-cols-2 lg:grid-cols-5">
              {EXCEL_SCOPE_OPTIONS.map((option) => (
                <label
                  className={`flex min-h-[42px] items-center gap-2 rounded-[4px] border px-3 text-sm font-bold ${
                    selectedScopes.includes(option.value)
                      ? "border-[#34a853] bg-[#eef8f1] text-[#1d6f38]"
                      : "border-slate-300 bg-white text-slate-600"
                  }`}
                  key={option.value}
                >
                  <input
                    checked={selectedScopes.includes(option.value)}
                    disabled={applyState === "applying"}
                    onChange={() => toggleScope(option.value)}
                    type="checkbox"
                  />
                  {option.label}
                </label>
              ))}
            </div>
            {selectedScopes.length === 0 ? (
              <p className="mt-2 text-sm font-semibold text-[#9f1239]">
                Select at least one scope to continue.
              </p>
            ) : null}
          </fieldset>
        </GrowerSurface>

        <div className="flex flex-wrap gap-2">
          <button
            className={`rounded-[4px] border px-4 py-2 text-sm font-bold ${
              activeTab === "import"
                ? "border-[#243245] bg-[#243245] text-white"
                : "border-slate-300 bg-white text-slate-700"
            }`}
            onClick={() => setActiveTab("import")}
            type="button"
          >
            Import
          </button>
          <button
            className={`rounded-[4px] border px-4 py-2 text-sm font-bold ${
              activeTab === "guidance"
                ? "border-[#243245] bg-[#243245] text-white"
                : "border-slate-300 bg-white text-slate-700"
            }`}
            onClick={() => setActiveTab("guidance")}
            type="button"
          >
            Workbook Notes
          </button>
        </div>

        {statusMessage ? (
          <div className="rounded-[8px] border border-[#34a853]/40 bg-[#eef8f1] px-4 py-3 text-sm font-semibold text-[#1d6f38]">
            {statusMessage}
          </div>
        ) : null}
        {errorMessage ? (
          <div role="alert" className="rounded-[8px] border border-[#c0262d]/40 bg-[#fff1f2] px-4 py-3 text-sm font-semibold text-[#9f1239]">
            {errorMessage}
          </div>
        ) : null}

        {activeTab === "guidance" ? (
          <GrowerSurface padding="p-5">
            <GrowerSectionHeading
              eyebrow="Workbook Notes"
              title="What The Import Supports Today"
              description="The backend accepts farms, fields, equipment, fertilizers, crop years, operations, planting, harvest, soil tests, fertilizer rates, and advanced 4R applications and practices."
            />
            <div className="mt-5 grid gap-4 md:grid-cols-2">
              <div className="rounded-[8px] border border-slate-200 bg-[#fcfcfa] p-4">
                <h3 className="font-black text-slate-900">Matching</h3>
                <p className="mt-2 text-sm leading-6 text-slate-600">
                  Exported public ID columns should stay untouched. If public
                  IDs are blank, matching falls back to supported workbook
                  names and crop year dependencies.
                </p>
                <p className="mt-2 text-sm leading-6 text-slate-600">
                  Import templates include highlighted example rows. Replace
                  or delete those examples before applying the workbook.
                </p>
              </div>
              <div className="rounded-[8px] border border-slate-200 bg-[#fcfcfa] p-4">
                <h3 className="font-black text-slate-900">Blank Cells</h3>
                <p className="mt-2 text-sm leading-6 text-slate-600">
                  Update leaves blank workbook fields untouched. Override also
                  clears fields the workbook left blank when the current record
                  has a value.
                </p>
                <p className="mt-2 text-sm leading-6 text-slate-600">
                  clear_boundaries is a boolean control. TRUE removes saved
                  boundaries after you remove that field’s Field Boundaries rows.
                  Blank or FALSE preserves them with either strategy.
                </p>
              </div>
            </div>
          </GrowerSurface>
        ) : (
          <div className="space-y-5">
            <GrowerSurface padding="p-5">
              <div>
                <div className="text-[12px] font-bold uppercase tracking-[0.16em] text-slate-400">
                  Import Workbook
                </div>
                <h2 className="mt-1 text-2xl font-black text-slate-900">
                  Preview And Apply
                </h2>
                <p className="mt-2 max-w-[720px] text-sm leading-6 text-slate-600">
                  Drop or choose an .xlsx workbook. Validation starts automatically;
                  Apply saves your selected changes.
                </p>
              </div>
              <input
                accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                className="hidden"
                onChange={(event) => {
                  if (event.target.files?.length) chooseWorkbook(event.target.files);
                }}
                ref={fileInputRef}
                type="file"
              />
              <button
                aria-label="Choose File"
                aria-describedby="excel-workbook-drop-help"
                aria-busy={previewState === "loading"}
                className={`mt-5 flex min-h-[170px] w-full flex-col items-center justify-center gap-2 rounded-[8px] border-2 border-dashed px-5 py-6 text-center transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-[#f15d22] disabled:cursor-not-allowed disabled:opacity-50 ${
                  isDraggingWorkbook
                    ? "border-[#f15d22] bg-[#fff3ed]"
                    : "border-slate-300 bg-[#fcfcfa] hover:border-[#f15d22] hover:bg-[#fff8f4]"
                }`}
                data-testid="excel-workbook-dropzone"
                disabled={!canSelectWorkbook}
                onClick={openWorkbookPicker}
                onDragEnter={(event) => {
                  if (!Array.from(event.dataTransfer.types).includes("Files")) return;
                  event.preventDefault();
                  if (canSelectWorkbook) {
                    dragDepthRef.current += 1;
                    setIsDraggingWorkbook(true);
                  }
                }}
                onDragOver={(event) => {
                  event.preventDefault();
                  event.dataTransfer.dropEffect = canSelectWorkbook ? "copy" : "none";
                }}
                onDragLeave={() => {
                  dragDepthRef.current = Math.max(0, dragDepthRef.current - 1);
                  if (!dragDepthRef.current) setIsDraggingWorkbook(false);
                }}
                onDragEnd={resetWorkbookDrag}
                onDrop={(event) => {
                  event.preventDefault();
                  resetWorkbookDrag();
                  if (canSelectWorkbook) chooseWorkbook(event.dataTransfer.files);
                }}
                type="button"
              >
                <span aria-hidden="true" className="text-[#f15d22]">
                  <FileUploadIcon sx={{ fontSize: 34 }} />
                </span>
                <span className="text-base font-bold text-slate-900">
                  {isDraggingWorkbook
                    ? "Drop workbook to preview"
                    : selectedFile
                      ? "Drop a replacement workbook here"
                      : "Drop your Excel workbook here"}
                </span>
                <span className="text-sm font-semibold text-[#c54410] underline underline-offset-4">
                  or choose a file
                </span>
                <span className="text-xs text-slate-500" id="excel-workbook-drop-help">
                  One .xlsx workbook · Up to 20 MB · Automatic preview
                </span>
              </button>
              {!canImportForSelectedGrower ? (
                <div className="mt-4">
                  <AggregatorScopeWarning
                    message="This selected grower link cannot preview or apply import workbooks."
                    requiredScope={IMPORT_WRITE_SCOPE}
                  />
                </div>
              ) : null}

              <div aria-live="polite" className="mt-3 flex flex-wrap items-center justify-between gap-2 text-sm text-slate-600" role="status">
                <span className="min-w-0 break-all">
                  Selected file:{" "}
                  <span className="font-bold text-slate-700">{selectedFile?.name || "None selected"}</span>
                </span>
                {previewState === "loading" ? <span>Previewing...</span> : null}
              </div>

              {previewRows.length ? (
                <div className="mt-5 border-t border-slate-200 pt-5">
                  <fieldset>
                    <legend className="text-sm font-black text-slate-900">
                      How should matched records be applied?
                    </legend>
                    <div className="mt-3 grid gap-3 lg:grid-cols-2">
                      {APPLY_STRATEGY_OPTIONS.map((option) => {
                        const selected = applyStrategy === option.value;
                        return (
                          <label
                            className={`flex cursor-pointer items-start gap-3 rounded-[6px] border p-4 ${
                              selected
                                ? "border-[#34a853] bg-[#eef8f1]"
                                : "border-slate-200 bg-white hover:border-slate-300"
                            }`}
                            key={option.value}
                          >
                            <input
                              checked={selected}
                              className="mt-1"
                              name="excel-apply-strategy"
                              onChange={() => {
                                setApplyStrategy(option.value);
                                setApplyResults([]);
                                setResolvedCases({});
                                setOverrideConfirmed(false);
                                setStatusMessage("");
                              }}
                              type="radio"
                              value={option.value}
                            />
                            <span>
                              <span className="block text-sm font-black text-slate-900">
                                {option.label}
                              </span>
                              <span className="mt-1 block text-sm leading-6 text-slate-600">
                                {option.description}
                              </span>
                            </span>
                          </label>
                        );
                      })}
                    </div>
                  </fieldset>
                  {applyStrategy === "override" && blankOverrideCount > 0 ? (
                    <label className="mt-4 flex items-start gap-3 rounded-[8px] border border-[#f15d22]/35 bg-[#fff3ed] p-4 text-sm leading-6 text-[#9a3514]">
                      <input
                        checked={overrideConfirmed}
                        className="mt-1"
                        onChange={(event) =>
                          setOverrideConfirmed(event.target.checked)
                        }
                        type="checkbox"
                      />
                      <span>
                        I understand Override will clear {blankOverrideCount}{" "}
                        populated {blankOverrideCount === 1 ? "field" : "fields"}{" "}
                        because the corresponding workbook cells are blank.
                      </span>
                    </label>
                  ) : null}
                  {actionablePreviewCount === 0 ? (
                    <div className="mt-4 rounded-[8px] border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-600">
                      No valid changes are available to apply. Correct the
                      workbook errors or add rows with new or changed data,
                      then drop or choose the saved workbook again.
                    </div>
                  ) : null}
                  <div className="mt-4 flex justify-end">
                    <ExcelButton
                      disabled={!canApply}
                      icon={<CheckCircleIcon fontSize="small" />}
                      onClick={handleApply}
                    >
                      {applyState === "applying"
                        ? "Applying..."
                        : applyStrategy === "override"
                          ? "Apply override"
                          : "Apply updates"}
                    </ExcelButton>
                  </div>
                </div>
              ) : null}

              {previewRows.length ? (
                <div className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
                  <MetricCard label="Rows" value={previewSummary.total} />
                  <MetricCard
                    label="New"
                    tone="green"
                    value={previewSummary.statuses.new || 0}
                  />
                  <MetricCard
                    label="Matched"
                    tone="gold"
                    value={previewSummary.statuses.matched || 0}
                  />
                  <MetricCard
                    label="Diffs"
                    tone="orange"
                    value={previewSummary.diffs}
                  />
                  <MetricCard
                    label="Errors"
                    tone={previewSummary.errors ? "orange" : "neutral"}
                    value={previewSummary.errors}
                  />
                </div>
              ) : null}

              {(hasPreviewErrors || diagnostics.length > 0) ? (
                <label className="mt-5 flex items-start gap-3 rounded-[8px] border border-[#f15d22]/35 bg-[#fff3ed] p-4 text-sm leading-6 text-[#9a3514]">
                  <input
                    checked={errorsAcknowledged}
                    className="mt-1"
                    onChange={(event) =>
                      setErrorsAcknowledged(event.target.checked)
                    }
                    type="checkbox"
                  />
                  <span>
                    I reviewed the preview errors. Applying will skip errored,
                    ambiguous, invalid, or missing-dependency rows and continue
                    with rows that can be processed.
                  </span>
                </label>
              ) : null}
            </GrowerSurface>

            {diagnostics.length > 0 && (
              <div role="alert" className="my-4 border-l-4 border-amber-500 bg-amber-50 p-4 text-sm">
                <p className="font-bold">Workbook items need attention</p>
                <ul>{diagnostics.map((item, index) => <li key={index}>
                  {item.sheet}{item.row_index ? `, row ${item.row_index}` : ""}{item.column ? `, ${item.column}` : ""}: {item.message}
                </li>)}</ul>
              </div>
            )}
            {attention.length > 0 && (
              <div role="alert" className="my-4 border-l-4 border-amber-500 bg-amber-50 p-4 text-sm">
                <p className="font-bold">{attention.every(item => !item.operation || item.operation === "analysis") ? "Attention required before analysis" : "Some operations need more information"}</p>
                <p>The records were saved. Complete these inputs only for the operation you want to use.</p>
                <ul>{attention.map((item) => <li key={`${item.operation || "analysis"}-${item.farm_id}-${item.field_id || "farm"}-${item.crop_year_id || "field"}`} className="mt-3">
                  <a className="font-bold underline" href={item.crop_year_id ? `/farm/${item.farm_id}/field/${item.field_id}/cropyear/${item.crop_year_id}/addanalysis` : item.field_id ? `/farm/${item.farm_id}/field/${item.field_id}/form` : `/farm/${item.farm_id}/form`}>{item.label} · {item.operation === "soil_enrichment" ? "Official soil estimate" : "Review inputs"}</a>
                  <ul>{item.reasons.map((reason) => <li key={reason}>{reason}</li>)}</ul>
                </li>)}</ul>
              </div>
            )}
            {previewRows.length ? (
              <PreviewRows rows={previewRows} />
            ) : (
              <GrowerEmptyState
                title={
                  previewState === "loading" ? "Checking Workbook" : hasPreviewed ? "No Importable Rows Found" : "No Workbook Preview Yet"
                }
                description={
                  previewState === "loading"
                    ? "Validation is read-only. You can drop or choose a replacement workbook while this preview runs."
                    : hasPreviewed
                      ? "The selected workbook has no data rows in the enabled scopes. Check the workbook sheets or change the selected scopes."
                      : "Drop or choose an Excel workbook to automatically see validation, matches, changes, and import blockers."
                }
              />
            )}

            {applyResults.length ? (
              <div
                className="scroll-mt-5 space-y-5"
                data-testid="excel-apply-results"
                ref={applyResultsRef}
              >
                <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-6">
                  <MetricCard label="Processed" value={applySummary.total} />
                  <MetricCard
                    label="Created"
                    tone="green"
                    value={applySummary.outcomes.created || 0}
                  />
                  <MetricCard
                    label="Updated"
                    tone="green"
                    value={applySummary.outcomes.updated || 0}
                  />
                  <MetricCard
                    label="Needs Review"
                    tone="gold"
                    value={applySummary.outcomes.case_opened || 0}
                  />
                  <MetricCard
                    label="Unchanged"
                    value={applySummary.outcomes.unchanged || 0}
                  />
                  <MetricCard
                    label="Skipped"
                    tone={
                      applySummary.outcomes.skipped_error ? "orange" : "neutral"
                    }
                    value={applySummary.outcomes.skipped_error || 0}
                  />
                </div>
                <ApplyResults
                  canResolve={canResolveForSelectedGrower}
                  onResolveCase={handleResolveCase}
                  previewRows={previewRows}
                  resolvingCase={resolvingCase}
                  resolvedCases={resolvedCases}
                  results={applyResults}
                  strategy={applyStrategy}
                />
              </div>
            ) : null}
          </div>
        )}
          </>
        )}
      </div>
    </Page>
  );
}
